import type { FC, MouseEvent } from 'react';
import { useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import { ScreenSizeContext } from '@wix/da-hooks/pkg/useMeasureElement/ScreenSizeContext';
import {
  getDeviationType,
  isTextualDeviation,
} from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import Dumbo from '@wix/da-shared-react/pkg/DeviationViews/Dumbo';
import ListItemDiv from '@wix/da-ds/pkg/List/ListItem/dsPresets/ListItemDiv';
import Checkbox from '@wix/da-ds/pkg/Checkbox';
import { useMainActionDeviationClick } from '../../../../../../../hooks/useMainActionDeviationClick';
import DeviationThumb from '../../../../../../StudioDeviationList/_partials/DeviationThumb';
import DeviationInfo from '../../../../../../StudioDeviationList/_partials/DeviationInfo';
import DeviationSaleIndicators from '../../../../../../StudioDeviationList/_partials/DeviationSaleIndicators';
import DeviationStats from '../../../../../../StudioDeviationList/_partials/DeviationStats';
import DeviationTextExcerpt from '../../../../../../StudioDeviationList/_partials/DeviationTextExcerpt';
import DeviationActions from '../../../../../../tableActions/DeviationActions';
import PublishedActionsMobile from '../../../../../../published/PublishedActionsMobile';
import { getActiveSection, getStudioParams } from '../../../../../../../selectors/studio';
import { StudioSection } from '../../../../../../../types/store';
import { Props } from '../../types';

import s from './DefaultLayout.scss';

const DefaultLayout: FC<Props> = ({
  deviation,
  offset,
  scrollToRef,
  isSelected,
  isInSelectMode,
  className,
  onChange,
}) => {
  const { width: screenSize } = useContext(ScreenSizeContext);
  const { onClick, mainAction } = useMainActionDeviationClick(deviation, scrollToRef, 'list-item');

  const onItemClick = useCallback(
    (ev: MouseEvent, clickedDeviation: PapiDeviation) => {
      // 1. When in selection mode, clicking the item selects it,
      // otherwise it takes the user to edit or view...
      if (!isInSelectMode) {
        // 2. ...but use preventDefault to prevent selection on the way...
        // 3. ...and also: useDuperbrowseLink won't open duper if ev.defaultPrevented
        //    (so don't do it for 'view')
        mainAction !== 'view' && ev.preventDefault();
        onClick(ev);
      }
    },
    [isInSelectMode, mainAction, onClick]
  );

  const deviationType = getDeviationType(deviation);
  const isTextual = isTextualDeviation(deviation);
  const isLiterature = deviationType === DeviationTypes.LITERATURE;
  const isStatusUpdate = deviationType === DeviationTypes.STATUS;
  const isLiteratureLetterCover = isLiterature && !deviation.coverImage;
  const showBackgroundCover = isLiterature || (isTextual && deviation.coverImage);
  const showThumb = !isTextual;
  const hasTallInfo = isTextual && !isStatusUpdate;

  const activeSection = useSelector(getActiveSection);
  const { contentType } = useSelector(getStudioParams);
  const showPublishedActions =
    !isInSelectMode && activeSection === StudioSection.PUBLISHED && contentType !== 'posts';

  const title = (
    <div className={s['title-wrapper']}>
      <div className={s['title']}>{deviation.title}</div>
      <DeviationSaleIndicators deviation={deviation} />
    </div>
  );

  const belowTitle = (
    <div className={s['below-title']}>
      {hasTallInfo && (
        <DeviationTextExcerpt className={s['excerpt']} deviation={deviation} noWrap />
      )}
      <DeviationInfo deviation={deviation} />
      <DeviationStats className={s['stats']} deviation={deviation} />
    </div>
  );

  return (
    <ListItemDiv
      className={cn(
        s['root'],
        isStatusUpdate && s['status-update'],
        showPublishedActions && s['with-actions'],
        className
      )}
      backgroundImage={
        showBackgroundCover ? (
          <Dumbo
            className={cn(
              s['background-cover'],
              isLiteratureLetterCover && s['background-cover-letter']
            )}
            deviation={deviation.coverImage || deviation}
            width={screenSize}
            useTextlessMode
          />
        ) : null
      }
      label={
        isStatusUpdate ? (
          <div className={s['label']}>
            <DeviationTextExcerpt className={s['excerpt']} deviation={deviation} noWrap />
            {belowTitle}
          </div>
        ) : (
          <div className={s['label']}>
            {title}
            {belowTitle}
          </div>
        )
      }
      prefix={
        <div className={s['prefix']}>
          <Checkbox inputClassName={s['input']} isChecked={isSelected} onChange={onChange} />
          {showThumb && <DeviationThumb deviation={deviation} />}
        </div>
      }
      suffix={
        isInSelectMode ? null : (
          <DeviationActions
            className={cn(s['actions'], hasTallInfo && s['actions-tall'])}
            deviation={deviation}
            offset={offset}
            onlyContextMenu
          />
        )
      }
      bottomSlot={showPublishedActions && <PublishedActionsMobile deviation={deviation} />}
      onLabelClick={ev => onItemClick(ev, deviation)}
      checked={isSelected}
      isLabelBold
    />
  );
};
DefaultLayout.displayName = 'DefaultLayout';

export default DefaultLayout;
